import React from 'react';
import Person from './cards/person';
import { currencyFormatter } from 'utils/formatters/currency';
import styles from './styles/filterMatch.module.css';

/**
 * Pretty-print a filter type key for display.
 */
const formatFilterType = (type) => {
  switch ((type || '').toLowerCase()) {
    case 'affiliation': return 'Affiliation';
    case 'constituency': return 'Constituency';
    case 'region': return 'Region';
    case 'prospect': return 'Prospect';
    case 'lifetime_giving': return 'Giving';
    case 'age': return 'Age';
    default: return type;
  }
};

/**
 * Format filter value(s) to a readable string.
 */
const formatFilterValue = (filter) => {
  const t = (filter.Type || filter.type || '').toLowerCase();
  const vals = filter.FilterValue || filter.filterValue;

  if (t === 'lifetime_giving') {
    const rangeFilters = filter.Filters || filter.filters || [];
    const min = rangeFilters[0]?.FilterValue ?? rangeFilters[0]?.filterValue ?? 0;
    const max = rangeFilters[1]?.FilterValue ?? rangeFilters[1]?.filterValue ?? 0;
    return `${currencyFormatter(min)} – ${currencyFormatter(max)}`;
  }

  if (t === 'age') {
    const rangeFilters = filter.Filters || filter.filters || [];
    const start = rangeFilters[0]?.FilterValue ?? rangeFilters[0]?.filterValue ?? 0;
    const end = rangeFilters[1]?.FilterValue ?? rangeFilters[1]?.filterValue ?? 120;
    return `${start} – ${end}`;
  }

  if (Array.isArray(vals)) {
    return vals.join(', ');
  }

  return String(vals ?? '');
};

/**
 * FilterSessionSummary – styled to match the existing relationship header dark card.
 */
const FilterSessionSummary = ({ data }) => {
  let filters = data?.filters || [];
  if (typeof filters === 'string') {
    try { filters = JSON.parse(filters); } catch { filters = []; }
  }
  const totalMatchCount = data?.totalMatchCount ?? data?.resultCount ?? 0;
  const displayedCount = data?.displayedCount ?? data?.resultCount ?? 0;
  const maxLimit = data?.maxLimit ?? displayedCount;
  const filterCount = filters.length;

  return (
    <div className={styles.session_card}>
      <div className={styles.session_header}>
        <span className={styles.session_icon} role="img" aria-label="filter">🔍</span>
        <span className={styles.session_type}>Filter Match connection</span>
      </div>
      <div className={styles.session_name}>filter_match</div>
      <div className={styles.session_subtitle}>
        {filterCount} filter{filterCount !== 1 ? 's' : ''} · {displayedCount.toLocaleString()} results
      </div>
      <div className={styles.applied_filters}>
        <div className={styles.applied_label}>APPLIED FILTERS</div>
        {filters.map((f, i) => (
          <div key={i} className={styles.filter_row}>
            <span className={styles.filter_type}>
              {formatFilterType(f.Type || f.type)}
            </span>
            <span className={styles.filter_value}>
              {formatFilterValue(f)}
            </span>
          </div>
        ))}
      </div>
      <div className={styles.stats_row}>
        <div className={styles.stat}>
          <span className={styles.stat_value}>{totalMatchCount.toLocaleString()}</span>
          <span className={styles.stat_label}>TOTAL</span>
        </div>
        <div className={styles.stat}>
          <span className={styles.stat_value}>{displayedCount.toLocaleString()}</span>
          <span className={styles.stat_label}>DISPLAYED</span>
        </div>
        <div className={styles.stat}>
          <span className={styles.stat_value}>{maxLimit.toLocaleString()}</span>
          <span className={styles.stat_label}>MAX LIMIT</span>
        </div>
      </div>
    </div>
  );
};

/**
 * FilterMatch – Content for the edge detail panel when a filter_match edge
 * is selected. Shows the filter session summary + the existing Person card.
 */
const FilterMatch = ({ topNode, bottomNode }) => {
  const isTopFilterSession = topNode?.nodeType === 'filter_session';
  const sessionNode = isTopFilterSession ? topNode : bottomNode;
  const personNode = isTopFilterSession ? bottomNode : topNode;

  return (
    <div className={styles.wrapper}>
      <FilterSessionSummary data={sessionNode} />
      {personNode?.name && (
        <Person properties={personNode} headerText="Matched Person" />
      )}
    </div>
  );
};

export default FilterMatch;
